import clsx from 'clsx'
import Image from 'next/image'
import { useRouter } from 'next/router'

import { Icon } from '@wartek-id/icon'
import { Link } from '@wartek-id/link'
import { List, ListItem } from '@wartek-id/list'
import { Popover, PopoverContent, PopoverTrigger } from '@wartek-id/popover'
import { Text } from '@wartek-id/text'
import { Badge } from '@wartek-id/badge'

import styleHeader from 'app/KinerjaShared/components/PageHeader/PageHeader.module.css'
import { useAuth } from 'utils/auth'
import usePageHeader from 'app/KinerjaShared/components/PageHeader/usePageHeader'
import { PENGELOLAAN_KINERJA_BERANDA_PATH } from 'app/KinerjaShared/constants/path'
import useToggleSdkContext from 'utils/useToogleSdk'

interface DemoPageHeaderProps {
  title?: string
  icon?: string
  className?: string
  children?: React.ReactNode
}

export const PageHeader = ({
  title = 'Pengelolaan Kinerja Guru',
  className = '',
  children,
}: DemoPageHeaderProps) => {
  const { session } = useAuth()
  const router = useRouter()

  const toggles = useToggleSdkContext()
  const isToggleNewLogoEnabled = toggles?.['kinerja-new-logo-enabled'] ?? false

  const { isMenuOpen, setIsMenuOpen } = usePageHeader()

  return (
    <div className="sticky top-0 z-20 w-full">
      <div
        className={clsx(
          'shadow-header',
          'flex justify-between items-center',
          'bg-[color:var(--color-gray-90)]',
          className
        )}
      >
        <div className="flex-none pt-3 pb-2.5 px-6">
          <Link
            className="!p-0 !leading-[0] flex items-center gap-3 !no-underline"
            href={PENGELOLAAN_KINERJA_BERANDA_PATH}
          >
            <Image
              src="/images/kemendikbud2x.png"
              width={40}
              height={40}
              alt="Logo Tut Wuri Handayani"
            />
            {isToggleNewLogoEnabled ? (
              <Image
                data-testid="kinerja-new-logo"
                src="/icon/e-pengelolaan-kinerja-logo.png"
                width={124}
                height={32}
                alt="Logo Pengelolaan-Kinerja"
                className="!select-none !pointer-events-none filter grayscale contrast-200 invert"
              />
            ) : (
              <>
                <Image
                  src="/icon/guru-penggerak.svg"
                  width={29}
                  height={40}
                  alt="Logo Merdeka Mengajar"
                />
                <Text
                  as="span"
                  variant="heading-md"
                  color="inverse"
                  className="!text-[color:var(--color-white)]"
                >
                  {title}
                </Text>
              </>
            )}

            <Badge variant="warning">Simulasi</Badge>
          </Link>
        </div>
        <div className="flex flex-none px-6">
          <Popover
            onOpen={() => setIsMenuOpen(true)}
            onClose={() => setIsMenuOpen(false)}
          >
            <PopoverTrigger aria-label="Menu" className="flex gap-3 pt-6 pb-5">
              <Icon className="!text-[color:var(--color-white)]">group</Icon>
              <Text variant="action" className="shrink-0" color="inverse">
                {session?.user?.name}
              </Text>
              <Icon
                className={clsx(
                  'transition-transform transform !text-[color:var(--color-white)]',
                  isMenuOpen && 'rotate-180'
                )}
              >
                expand_more
              </Icon>
            </PopoverTrigger>

            <PopoverContent
              className={clsx(
                'transition-all mt-3 max-w-xs min-w-[20rem]',
                styleHeader.container
              )}
            >
              <List type="menu" className="!p-0">
                <ListItem
                  className={clsx(
                    '!p-4 bg-default flex flex-col rounded-t-lg',
                    styleHeader.listItem
                  )}
                >
                  <Text
                    as="div"
                    variant="action"
                    className="text-left overflow-wrap-anywhere"
                  >
                    {session?.user?.name}
                  </Text>
                  <Text
                    as="div"
                    color="subdued"
                    className="overflow-wrap-anywhere"
                  >
                    {session?.user?.email}
                  </Text>
                </ListItem>
                {children ? children : <></>}
                <ListItem
                  className={clsx('!px-4 !py-3', styleHeader.listItem)}
                  onClick={() => router.push('/logout')}
                >
                  <Text variant="body">Keluar</Text>
                </ListItem>
              </List>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="p-4 rounded-b bg-orange-30">
        <Text>
          Sistem ini hanya untuk simulasi atau keperluan masa percobaan dan
          bukan merupakan sistem yang sebenarnya.
        </Text>
      </div>
    </div>
  )
}
